import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { newsArticles, categories } from '../data/newsArticles';

const slugify = (text) => {
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

const NewsRoom = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 5;

  // Filter articles by category
  const filteredArticles = useMemo(() => {
    if (selectedCategory === 'All') {
      return newsArticles;
    }
    return newsArticles.filter(article => article.category === selectedCategory);
  }, [selectedCategory]);

  // Paginate articles
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
      month: months[date.getMonth()],
      day: date.getDate(),
      year: date.getFullYear()
    };
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            News Room
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto">
            Stay updated with the latest news, performances, and announcements from Northwest Ballet
          </p>
        </div>
      </div>

      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-4">
            {/* Category Filter */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-3">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                      selectedCategory === category
                        ? 'bg-ballet-burgundy text-white shadow-lg'
                        : 'bg-white text-ballet-burgundy hover:bg-ballet-light border border-ballet-burgundy/20'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Articles */}
            <div className="space-y-8">
              {currentArticles.map((article) => {
                const dateInfo = formatDate(article.date);
                return (
                  <Link to={`/news/${slugify(article.title)}`} key={article.id} className="flex flex-col block card-elevated overflow-hidden group">
                    <article className="flex-grow">
                      <div className="p-8">
                        <div className="flex items-start space-x-6">
                          {/* Date Badge */}
                          <div className="flex-shrink-0 text-center bg-ballet-burgundy text-white rounded-xl p-4 min-w-[80px]">
                            <div className="text-sm font-medium">{dateInfo.month}</div>
                            <div className="text-2xl font-bold">{dateInfo.day}</div>
                            <div className="text-sm">{dateInfo.year}</div>
                          </div>

                          {/* Article Content */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-4 mb-3">
                              <span className="inline-block px-3 py-1 bg-ballet-light text-ballet-burgundy text-sm font-medium rounded-full">
                                {article.category}
                              </span>
                              {article.featured && (
                                <span className="inline-block px-3 py-1 bg-ballet-gold text-white text-sm font-medium rounded-full">
                                  Featured
                                </span>
                              )}
                            </div>

                            <h2 className="text-2xl lg:text-3xl font-elegant font-bold text-ballet-burgundy mb-3 group-hover:text-ballet-gold transition-colors duration-200">
                              {article.title}
                            </h2>

                            <p className="text-gray-700 text-lg leading-relaxed mb-4">
                              {article.excerpt.length > 150
                                ? `${article.excerpt.substring(0, 150)}...`
                                : article.excerpt}
                            </p>

                            <span className="btn-primary">
                              Read More
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-xl bg-white text-ballet-burgundy border border-ballet-burgundy/20 hover:bg-ballet-light disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Previous
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                          currentPage === pageNum
                            ? 'bg-ballet-burgundy text-white'
                            : 'bg-white text-ballet-burgundy border border-ballet-burgundy/20 hover:bg-ballet-light'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-xl bg-white text-ballet-burgundy border border-ballet-burgundy/20 hover:bg-ballet-light disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default NewsRoom;
